import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend
} from "recharts";
import { 
  Activity, AlertTriangle, Clock, BarChart3, 
  TrendingUp, FileCheck2, Shield, LayoutPanelTop, 
  Plus, CheckCircle, X
} from "lucide-react";
import Topbar from "../../components/TopbarNav";        
import HOSidebar from "../../components/MasterSideBar";  
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { fetchProjectPortfolioStatus } from "../../api/projectApi";
import {
  getDashboardStats,
  getComplianceStats,
  getProcurementMethodDistribution,
  getComplianceTrend,
  getRiskAlerts
} from "../../api/procurementPlanApi";
import { motion, AnimatePresence } from "framer-motion";

// Custom tooltip for procurement methods
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 border border-gray-100 rounded-[1.5rem] shadow-2xl">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-2">{payload[0].name}</p>
        <p className="text-lg font-black text-gray-900">{`Value: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

export default function HoCDashboard() {
  const navigate = useNavigate();
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [kpis, setKpis] = useState([
    { title: "Total Projects", value: "0", icon: <BarChart3 />, description: "Full agency scope" },
    { title: "Compliance", value: "0%", icon: <Shield />, description: "Project compliance rate" },
    { title: "At Risk", value: "0", icon: <AlertTriangle />, description: "Immediate attention required" },
    { title: "Pending", value: "0", icon: <Clock />, description: "Awaiting approval" },
  ]);
  const [projectStatus, setProjectStatus] = useState([]);
  const [complianceTrend, setComplianceTrend] = useState([]);
  const [procurementMethods, setProcurementMethods] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [riskAlerts, setRiskAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const colorMap = {
    "On Track": "#4C51BF",
    "At Risk": "#ED8936",
    "Delayed": "#FDA4AF",
    "Completed": "#48BB78",
  };

  // Fetch all dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        const [projectData, dashboardStats, complianceStats, trendData, methodsData, alertsData] = await Promise.all([
          fetchProjectPortfolioStatus(),
          getDashboardStats(),
          getComplianceStats(),
          getComplianceTrend(),
          getProcurementMethodDistribution(),
          getRiskAlerts()
        ]);
        
        const complianceRate = complianceStats.totalPlans ? 
          Math.round((complianceStats.compliantPlans / complianceStats.totalPlans) * 100) : 0;
        
        setKpis([
          { 
            title: "Project Volume", 
            value: projectData.totalProjects?.toString() || "0", 
            icon: <Activity className="text-orange-600" />, 
            iconBg: "bg-orange-50",
            description: "Active lifecycle"
          },
          { 
            title: "Compliance Rate", 
            value: `${complianceRate}%`, 
            icon: <Shield className="text-emerald-600" />, 
            iconBg: "bg-emerald-50",
            description: "Regulatory benchmark"
          },
          { 
            title: "Alert Density", 
            value: projectData.atRiskCount?.toString() || "0", 
            icon: <AlertTriangle className="text-red-600" />, 
            iconBg: "bg-red-50",
            description: "Immediate intervention"
          },
          { 
            title: "Queue Status", 
            value: dashboardStats.pendingApprovals?.length?.toString() || "0", 
            icon: <Clock className="text-blue-600" />, 
            iconBg: "bg-blue-50",
            description: "Awaiting review"
          },
        ]);
        
        setProjectStatus(
          projectData.projectStatus?.map(item => ({
            name: item.name,
            value: item.value,
            color: colorMap[item.name] || "#6B7280"
          })) || []
        );
        
        setComplianceTrend(trendData || []);
        setProcurementMethods(methodsData || []);
        
        const formattedApprovals = dashboardStats.pendingApprovals?.map(plan => ({
          id: plan.planReference || plan.id,
          type: "Procurement Plan",
          status: plan.overallStatus,
          priority: plan.risk
        })) || [];
        
        setPendingApprovals(formattedApprovals);
        setRiskAlerts(alertsData || []);
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        setLoading(false);
      }
    };
    
    loadDashboardData();
  }, []);

  return (
    <div className="flex bg-gray-50 min-h-screen font-sans overflow-hidden">
      <HOSidebar 
        visible={sidebarVisible} 
        onClose={() => setSidebarVisible(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />
      
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarVisible(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            <div className="flex flex-col items-center text-center mb-12">
              <PremiumHeader 
                title="Operations" 
                lastWordAccent="Compliance" 
                subtitle="High-level oversight of global operations, risk management, and regulatory compliance." 
              />
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate("/project-compliance")}
                  className="flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 shadow-2xl shadow-gray-200 transition-all active:scale-95"
                >
                  <Shield size={16} strokeWidth={3} /> Execute Compliance Audit
                </motion.button>
              </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-40 bg-white rounded-[2rem] animate-pulse border border-gray-100 shadow-sm" />
                ))
              ) : (
                kpis.map((kpi, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <PremiumStatCard {...kpi} />
                  </motion.div>
                ))
              )}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-[360px] bg-white rounded-[2.5rem] animate-pulse border border-gray-100 shadow-sm" />
                ))
              ) : (
                <>
                  <PremiumCard title="Compliance Trajectory" icon={<TrendingUp size={18} />}>
                    <div className="h-[300px] pt-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={complianceTrend} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8fafc" />
                          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94a3b8' }} />
                          <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94a3b8' }} />
                          <Tooltip 
                            formatter={(value) => [`${value}%`, 'Compliance']}
                            contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="rate" 
                            stroke="#f97316" 
                            strokeWidth={4} 
                            dot={{ r: 5, fill: '#fff', strokeWidth: 3, stroke: '#f97316' }}
                            activeDot={{ r: 7, strokeWidth: 0, fill: '#f97316' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </PremiumCard>

                  <PremiumCard title="Portfolio Lifecycle Status" icon={<LayoutPanelTop size={18} />}>
                    <div className="h-[300px] py-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie 
                            data={projectStatus} 
                            dataKey="value" 
                            nameKey="name" 
                            outerRadius={95} 
                            innerRadius={65}
                            paddingAngle={8}
                            cx="50%"
                            cy="45%"
                            stroke="none"
                          >
                            {projectStatus.map((entry, i) => <Cell key={i} fill={entry.color} cornerRadius={12} />)}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} />
                          <Legend verticalAlign="bottom" iconType="circle" formatter={(value) => <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{value}</span>} wrapperStyle={{ paddingTop: '20px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </PremiumCard>

                  <PremiumCard title="Procurement Strategy Mix" icon={<BarChart3 size={18} />}>
                    <div className="h-[300px] py-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie 
                            data={procurementMethods} 
                            dataKey="value" 
                            nameKey="name" 
                            outerRadius={95} 
                            innerRadius={65}
                            paddingAngle={8}
                            cx="50%"
                            cy="45%"
                            stroke="none"
                          >
                            {procurementMethods.map((entry, i) => <Cell key={i} fill={entry.color} cornerRadius={12} />)}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend 
                            verticalAlign="bottom" 
                            iconType="circle"
                            formatter={(value) => <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{value.length > 15 ? `${value.substring(0, 15)}...` : value}</span>}
                            wrapperStyle={{ paddingTop: '20px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </PremiumCard>
                </>
              )}
            </div>

            {/* Approvals + Risk Alerts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {loading ? (
                Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="h-80 bg-white border border-gray-100 rounded-[2.5rem] animate-pulse shadow-sm" />
                ))
              ) : (
                <>
                  <PremiumCard title={`Strategic Approvals Queue (${pendingApprovals.length})`} icon={<FileCheck2 size={18} />}>
                    <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar pr-2 pt-2">
                      {pendingApprovals.length > 0 ? (
                        pendingApprovals.map((approval) => (
                          <div key={approval.id} className="group flex items-center justify-between p-5 border border-gray-50 rounded-2xl hover:bg-gray-50/50 transition-all">
                            <div className="flex-1">
                              <div className="font-black text-xs text-gray-800 uppercase tracking-widest">{approval.id}</div>
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em] mt-2">{approval.type} • {approval.status}</div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest ${
                                approval.priority === "Critical" ? "bg-red-50 text-red-600" :
                                approval.priority === "High" ? "bg-orange-50 text-orange-600" :
                                "bg-gray-50 text-gray-500"
                              }`}>
                                {approval.priority}
                              </span>
                              <button className="p-2.5 rounded-xl bg-orange-50 text-orange-600 opacity-0 group-hover:opacity-100 transition-all hover:bg-orange-100 active:scale-95 shadow-sm">
                                <CheckCircle size={16} strokeWidth={3} />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-20">
                          <CheckCircle size={48} strokeWidth={1} className="mx-auto text-orange-100 mb-6" />
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Decision queue is empty</p>
                        </div>
                      )}
                    </div>
                  </PremiumCard>

                  <PremiumCard title="Risk & Compliance Intelligence" icon={<Shield size={18} />}>
                    <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar pr-2 pt-2">
                      {riskAlerts.length > 0 ? (
                        riskAlerts.map((alert) => (
                          <div key={alert.id} className="flex items-start justify-between p-5 border border-gray-50 rounded-2xl hover:bg-gray-50/50 transition-all">
                            <div className="flex-1 mr-4">
                              <div className="font-black text-xs text-gray-800 uppercase tracking-widest">{alert.title}</div>
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em] mt-2 flex items-center gap-2">
                                <Clock size={10} /> Escalation Due: {alert.due}
                              </div>
                            </div>
                            <span className={`shrink-0 px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest ${
                              alert.severity === "Critical" ? "bg-red-50 text-red-600" :
                              alert.severity === "High" ? "bg-orange-50 text-orange-600" :
                              "bg-gray-50 text-gray-500"
                            }`}>
                              {alert.severity}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-20">
                          <Shield size={48} strokeWidth={1} className="mx-auto text-orange-100 mb-6" />
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">System remains fully compliant</p>
                        </div>
                      )}
                    </div>
                  </PremiumCard>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
